import UserOnboarding from './User';
import ExchangeConnection from './Exchange';
import InfoOnboarding from './Info';
import PagesOnboarding from './Pages';
import Redirect from './Redirect';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditUserOnboardingStepMutation } from '../../../store/services/api';
import { useDispatch } from 'react-redux';
import { setUserId as setStoreUserId } from '../../../store/slices/user';

const Onboarding = ({
  userId: passedUserId,
  onCancel,
  activeOnboardingStep,
}: {
  userId: number | null;
  onCancel: () => void;
  activeOnboardingStep?: number;
}) => {
  const [editUserOnboardingStep] = useEditUserOnboardingStepMutation();
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(1);
  const [userId, setUserId] = useState<number | null>(passedUserId);

  useEffect(() => {
    if (passedUserId) {
      setUserId(passedUserId);
    }
  }, [passedUserId]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const firstStepContainer = useRef<HTMLDivElement>(null);
  const secondStepContainer = useRef<HTMLDivElement>(null);
  const thirdStepContainer = useRef<HTMLDivElement>(null);
  const forthStepContainer = useRef<HTMLDivElement>(null);

  const moveToStepTwo = useCallback(
    (createdUserId?: number) => {
      scrollRef.current?.scrollTo({
        left: (firstStepContainer.current?.clientWidth || 0) + 45,
        behavior: 'smooth',
      });

      const id = createdUserId ?? userId;

      if (id) {
        editUserOnboardingStep({ userId: id, onboardingStep: 2 });
        setUserId(id);
      }

      setTimeout(() => {
        setActiveStep(2);
      }, 400);
    },
    [editUserOnboardingStep, userId],
  );

  const moveToStepThree = useCallback(() => {
    scrollRef.current?.scrollTo({
      left:
        (firstStepContainer.current?.clientWidth || 0) +
        (secondStepContainer.current?.clientWidth || 0) +
        90,
      behavior: 'smooth',
    });

    userId && editUserOnboardingStep({ userId, onboardingStep: 3 });

    setTimeout(() => {
      setActiveStep(3);
    }, 400);
  }, [editUserOnboardingStep, userId]);

  const moveToStepFour = useCallback(() => {
    scrollRef.current?.scrollTo({
      left:
        (firstStepContainer.current?.clientWidth || 0) +
        (secondStepContainer.current?.clientWidth || 0) +
        (thirdStepContainer.current?.clientWidth || 0) +
        135,
      behavior: 'smooth',
    });

    userId && editUserOnboardingStep({ userId, onboardingStep: 4 });

    setTimeout(() => {
      setActiveStep(4);
    }, 400);
  }, [editUserOnboardingStep, userId]);

  const moveToStepFive = useCallback(() => {
    scrollRef.current?.scrollTo({
      left:
        (firstStepContainer.current?.clientWidth || 0) +
        (secondStepContainer.current?.clientWidth || 0) +
        (thirdStepContainer.current?.clientWidth || 0) +
        (forthStepContainer.current?.clientWidth || 0) +
        180,
      behavior: 'smooth',
    });

    userId && editUserOnboardingStep({ userId, onboardingStep: null });

    setTimeout(() => {
      setActiveStep(5);
    }, 400);
  }, [editUserOnboardingStep, userId]);

  useEffect(() => {
    setTimeout(() => {
      switch (activeOnboardingStep) {
        case 2:
          moveToStepTwo();
          break;

        case 3:
          moveToStepThree();
          break;

        case 4:
          moveToStepFour();
          break;

        case 5:
          moveToStepFive();
          break;
      }
    }, 400);
  }, [activeOnboardingStep]);

  const handleFinish = () => {
    if (!userId) {
      return;
    }

    dispatch(setStoreUserId(userId));
  };

  return (
    <div
      style={{
        width: '100vw',
        display: 'flex',
        overflowX: 'hidden',
        padding: 150,
      }}
      ref={scrollRef}
    >
      <UserOnboarding
        moveToStepTwo={moveToStepTwo}
        isActive={activeStep === 1}
        ref={firstStepContainer}
        handleBackPress={onCancel}
      />
      <ExchangeConnection
        userId={userId}
        moveToStepThree={moveToStepThree}
        isActive={activeStep === 2}
        ref={secondStepContainer}
      />
      <InfoOnboarding
        userId={userId}
        moveToStepFour={moveToStepFour}
        isActive={activeStep === 3}
        ref={thirdStepContainer}
      />
      <PagesOnboarding
        moveToStepFive={moveToStepFive}
        isActive={activeStep === 4}
        ref={forthStepContainer}
        handleBackPress={moveToStepThree}
      />
      <Redirect
        handleFinish={handleFinish}
        isActive={activeStep === 5}
        handleBackPress={moveToStepFour}
      />
      <div
        style={{
          flex: '0 0 auto',
          height: '100%',
          width: '100vw',
        }}
      ></div>
    </div>
  );
};

export default Onboarding;

/**
 * Start onboarding:
 * 1. Ask for Name, email, password x2
 * 2. Add first exchange connection, do we allow the user to add multiple?
 * 3. Ask for extra information, like the option to get mails about orders, give some info
 * 4. Done, redirect to discover page
 *
 * add info on how to do the exchange connection
 * dropdown works correctly
 */
